import {Component, EventEmitter, Input, OnInit, Output, OnDestroy} from '@angular/core';
import {animate, style, transition, trigger} from "@angular/animations";
import {BehaviorSubject, Subject, Subscription, timer} from 'rxjs';
import {filter, finalize} from 'rxjs/operators';

import {SimpleWord} from '../../models/simple-word.model';
import {MP_RESULT} from '../msg-popup/msg-popup.component';
import {CordovaService} from '../../services/cordova/cordova.service';
import {RESULT_SPICE, TfSpiceService} from '../../services/tf-service/tf-spice.service';
import {VoiceService} from '../../services/voice/voice.service';
import {OptionService} from '../../services/option/option.service';
import {PitchService} from '../../services/pitch/pitch.service';
import {LayoutMgrService} from '../../services/layout-mgr/layout-mgr.service';
import {getWavAmplitude, rmsNormalization} from '../../defines/wav_process.func';
import {getMidPitch} from "../../defines/pitch.func";
import {api_url} from "../../defines/protocol";
import {
  DEFAULT_VOICE_AVG,
  LIST_RECORDER_CONFIG,
  RECORD_STOP_TIMEOUT,
  RECORDER_CONFIG,
  SAMPLE_RATE
} from '../../defines/pitch.const';
import {RecorderService} from "../../services/recorder/recorder.service";
// import {samplePitches} from '../../defines/dummy.const'; // 테트용 더미 데이터 포함

const offlineCtx = new OfflineAudioContext(1, SAMPLE_RATE, SAMPLE_RATE);

@Component({
  selector: 'app-word-pitch-popup',
  templateUrl: './word-pitch-popup.component.html',
  styleUrls: ['./word-pitch-popup.component.scss'],
  animations: [
    trigger('PitchInOut', [
      transition(':enter', [
        style({transform: 'translateY(100%)'}),
        animate('150ms ease-in', style({transform: 'translateY(0%)'}))
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({transform: 'translateY(50%)'}))
      ])
    ]),
  ],
})
export class WordPitchPopupComponent implements OnInit, OnDestroy {
  @Output() closePopup: EventEmitter<any>;
  @Input() word: SimpleWord;

  protected readonly listRecorderConfig = LIST_RECORDER_CONFIG;

  public show = false;

  protected get cordova(): Cordova {
    return this.cordovaSvc.cordova as Cordova;
  }

  // data1 = samplePitches;
  protected data1: number[] = [];

  protected data2: number[] = [];

  // TFL 초기화 되었다는 처리
  private tflInited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // 소리 재생/녹음 관련
  public get recording(): boolean {
    return this.recorderSvc.isRecording;
  }

  protected GetRecordBtnClass(): string {
    return this.isProcessing ? 'ing' : (this.recording ? 'on' : '');
  }

  private recordSrc: string;
  private recordPath: string;
  public recorderConfig: RECORDER_CONFIG = null;
  public userConfig: RECORDER_CONFIG = {
    voiceAvg: DEFAULT_VOICE_AVG,
  };

  protected stop$: Subscription | null = null;
  private recordStop$ = new Subject<void>();
  private recordStopTimeout$?: Subscription;

  protected isProcessing = false; // 녹음 시작 중, 혹은 녹음 종료 중

  constructor(
    protected cordovaSvc: CordovaService,
    protected tfSpiceSvc: TfSpiceService,
    protected voiceSvc: VoiceService,
    protected optionSvc: OptionService,
    protected recorderSvc: RecorderService,
    public pitchSvc: PitchService,
    public lm: LayoutMgrService,
  ) {
    this.closePopup = new EventEmitter<any>();

    cordovaSvc.deviceready.subscribe(() => {
      if (cordovaSvc.isAndroid) {
        this.recordSrc = this.recordPath = this.cordova.file.cacheDirectory + 'record.aac';
      }
      else {
        // iOS
        this.recordSrc = 'record.wav';
        this.recordPath = this.cordova.file.tempDirectory + 'record.wav';
      }
      console.log('ondeviceready, Media =', Media, this.recordSrc, this.recordPath);

      // onClickRecord() 쪽으로 초기화 이동.
      // NOTE: 안드로이드에서는 녹음 할 때마다, 아래 초기화 코드를 불러줘야 함.
      // NOTE: 안드로이드에서는 녹음 할 때마다, 아래 초기화 코드를 불러줘야 함.
      // this.recorderSvc.init(this.recordSrc);

      this.tfSpiceSvc.init()
        .then(() => {
          console.log('TFL SPICE init success');
          this.tflInited$.next(true);
          this.tflInited$.complete();
        })
        .catch(reason => console.error('TFL SPICE init fail:', reason));
    });
  }

  ngOnInit(): void {
    if (this.word?.wordVoice) {
      this.init(this.word.wordVoice);
    }
  }

  ngOnDestroy(): void {
    // 컴포넌트 소멸 시 즉시 isProcessing을 false로 설정
    this.isProcessing = false;

    // 녹음 중지 (recorderSvc.stop()의 반환 타입에 따라 수정 필요)
    if (this.recording) {
      // this.onStopRecord();  // 너무 무겁다.
      this.recorderSvc.stop().subscribe({
        complete: () => {
          console.log('Recording stopped.');
        }
      });
    }

    // 음성 재생 중지
    this.voiceSvc.Stop();

    // 녹음 중지 타임아웃 구독 해제
    this.recordStopTimeout$?.unsubscribe();
    this.recordStopTimeout$ = null;

    // stop$ 구독 해제 (onStopRecord() 호출 여부와 관계없이 해제)
    this.stop$?.unsubscribe();
    this.stop$ = null;

    // recordStop$ 구독 해제
    this.recordStop$?.unsubscribe();
    this.recordStop$ = null;
  }

  protected init(urlVoice: string | null  = null, voiceExIdx: number | null = null): void {
    console.warn('WordPitchPopupComponent::init() this.word =', this.word, voiceExIdx);

    if (this.cordova && urlVoice) {
      this.recorderConfig = LIST_RECORDER_CONFIG[this.word.recorder];

      this.cordova && this.tflInited$.pipe(
        filter(inited => inited)
      ).subscribe((inited) => {
        // TFL 초기화 완료 + this.word 도 초기화 완료

        const fullUrl = api_url(urlVoice);
        console.log('tflInited$ send true', urlVoice, fullUrl);

        this.drawWordVoice(fullUrl).then(() => {
          if (this.optionSvc.autoPlay) {
            this.voiceSvc.PlaySound(this.word, voiceExIdx).subscribe();
          }
        });
      });

      // 녹음 중지 처리
      this.recordStop$.subscribe(() => this.onStopRecord());
    }

    this.show = true;

    console.warn('WordPitchPopupComponent::init() this =', this);
  }

  public onClose(param: boolean = null): void {
    // 녹음 시작 중, 녹음 종료 중에는 팝업 닫기 불가. (아니면 너무 처리가 복잡해진다)
    if (!this.isProcessing) {
      this.closePopup.emit();
    }
  }

  async onClickRecord($event: MouseEvent): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.isProcessing) {
      this.isProcessing = true;

      if (!this.recording) {
        try {
          // 마이크 권한 확인 후 녹음 시작
          const isAuthorized = await this.cordovaSvc.isMicrophoneAuthorized();
          if (isAuthorized) {
            await this.startRecording();
          } else {
            const status = await this.cordovaSvc.requestMicrophoneAuthorization();
            if (status === this.cordovaSvc.diagnostic.permissionStatus.GRANTED) {
              await this.startRecording();
            } else {
              // 마이크 권한 거부 시 오류 처리
              this.handleMicrophoneError('Microphone authorization denied');
            }
          }
        } catch (error) {
          // 마이크 권한 확인/요청 중 오류 발생 시 처리
          this.handleMicrophoneError('Error checking/requesting microphone permission:', error);
        } finally {
          // 마이크 권한 획득 여부와 관계없이 isProcessing을 false로 설정
          this.isProcessing = false;
        }
      } else if (this.stop$ === null) {
        this.recordStopTimeout$?.unsubscribe();
        this.recordStopTimeout$ = null;

        this.recordStop$.next();
      }
    }
  }

  /**
   * 마이크 권한 관련 오류 처리 함수
   * @param message 오류 메시지
   * @param error 오류 객체 (선택 사항)
   */
  private handleMicrophoneError(message: string, error?: any): void {
    console.warn(message, error);

    const title = '마이크 오류';
    const os = this.cordovaSvc.platform ?? '';
    const errorMessage = `앱이 마이크를 사용 할 권한이 없습니다.<br>${os} OS 설정에 들어가셔서 마이크 설정을 해 주세요.`;

    this.lm.OpenMsgPopupAlert(errorMessage, title).subscribe(data => console.log(data));
  }

  private async startRecording(): Promise<void> {
    // 소리 재생 중지.
    this.voiceSvc.Stop();

    // 녹음 시작
    this.recorderSvc.init(this.recordSrc);

    try {
      await this.recorderSvc.start(); // 성공 정보를 받을 수 없다.
    } catch (error) {
      console.error('Error starting recording:', error);
      // 오류 처리 (예: 사용자에게 오류 메시지 표시)
      throw error; // 에러를 다시 던져서 onClickRecord에서 처리하도록 함
    }

    this.recordStopTimeout$ = timer(RECORD_STOP_TIMEOUT).subscribe(() => this.recordStop$.next());
  }

  /**
   * 녹음 중지 처리 함수
   */
  private onStopRecord(): void {
    // 녹음 종료
    this.stop$?.unsubscribe(); // 기존 구독 해지
    this.stop$ = this.recorderSvc.stop().pipe(
      finalize(() => this.stop$ = null),
    ).subscribe(() => {

      // console.warn('recordPath = ', this.recordPath);
      const parsePath = async (path: string) => {
        // console.warn("parsePath()", path);
        const arrayBuffer: ArrayBuffer = await this.parseRecord(path);

        this.data2 = await this.processBuffer(arrayBuffer, true);
        const mid = getMidPitch(this.word.iType, this.data2);
        if (!isNaN(mid)) {
          this.userConfig.voiceAvg = this.pitchSvc.setMid(mid);
          console.log('voiceAvg = ', this.userConfig.voiceAvg);
        }
        else {
          // TODO: 녹음 시간이 너무 짧거나, 소리가 작다는 경고 표시하기.
          console.error('PitchPopupComponent::onClickRecord() mid is NaN');

          this.lm.OpenMsgPopup({
            title: '음성분석 오류',
            msg: '잘 못 들었습니다.<br />다시 말씀하여 주십시오.',
            btnOk: '확인',
          }).subscribe();
        }
      };

      parsePath(this.recordPath)
        .catch(reason => console.error('parsePath:', reason))
        .finally(() => {
          this.isProcessing = false;
          this.stop$?.unsubscribe();
          this.stop$ = null;
        });
    })
  }


  private async load(url: string): Promise<ArrayBuffer> {
    console.log('PitchPopupComponent::load() url =', url);

    const response: Response = await fetch(url);
    const buffer: ArrayBuffer = await response.arrayBuffer();
    return buffer;
  }

  protected async drawWordVoice(urlVoice: string): Promise<void> {
    // console.warn('PitchPopupComponent::drawWordVoice() this.word =', this.word);

    if (!urlVoice) {
      throw new Error(`PitchPopupComponent::drawWordVoice() urlVoice is null ${this.word}`);
    }

    if ((this.data1?.length ?? 0) === 0) {
      const fullUrl =api_url(urlVoice);

      const buffer = await this.load(fullUrl);
      // console.warn('PitchPopupComponent::drawWordVoice() buffer =', buffer);

      this.data1 = await this.processBuffer(buffer);
      // console.warn('PitchPopupComponent::drawWordVoice() this.data1 =', this.data1);
    }
  }

  private async processBuffer(arrayBuffer: ArrayBuffer, normalize = false): Promise<Array<number>> {

    // const buffer: Buffer = new Buffer(arrayBuffer);
    // console.log('buffer =', arrayBuffer, buffer);

    const decodedData: AudioBuffer = await offlineCtx.decodeAudioData(arrayBuffer);
    // console.log('decodedData: AudioBuffer = ', decodedData);

    let channelData: Float32Array = decodedData.getChannelData(0);

    if (normalize) {
      // RMS 정규화 적용
      channelData = rmsNormalization(channelData, 0.15);
    }

    // const fArray: Array<number> = Array.from(channelData);
    // console.log('RAW channel data = ', fArray);

    // TFLiteSpice 예측 및 오류 처리
    let resultSpice: RESULT_SPICE;
    try {
      resultSpice = await this.tfSpiceSvc.predict(channelData);
    } catch (error) {
      console.error('Error predicting with TFLiteSpice:', error);

      throw error;
    }

    // this.data1 = resultSpice.pitches;
    // this.data2 = resultSpice.uncertainties;

    const fArray: Array<number> = Array.from(channelData);
    const amplitudes = getWavAmplitude(fArray);
    // console.log('amplitudes = ', amplitudes, fArray);
    // this.data2 = amplitudes;

    const data = resultSpice.uncertainties.map((uncertainty, idx) => {
        const amplitude = amplitudes[idx];
        const cond = amplitude > 0.05 && uncertainty < 0.6 && uncertainty - amplitude < 0.2;
        const ret = cond ? resultSpice.pitches[idx] : NaN;
        // console.log('map = ', idx, uncertainty, amplitudes[idx], ret);
        return ret;
      });
    return Promise.resolve(this.trimNaN(data));
    // return Promise.resolve(data);
  }

  private async parseRecord(path: string): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      window.resolveLocalFileSystemURL(path, (fileEntry: FileEntry) => {
        console.log('window.resolveLocalFileSystemURL(fileEntry ->)', fileEntry);

        fileEntry.file(file => {
          console.log('fileEntry.file(file ->)', file);
          const reader = new FileReader();

          reader.onloadend = function(e): any {
            console.log('reader.readArrayBuffer().onloadend(e ->)', e, this.result);
            resolve(this.result as ArrayBuffer);
          };
          reader.onerror = (e): any => reject(e);
          reader.readAsArrayBuffer(file);
        }, (err: FileError) => reject(err));
      }, (err: FileError) => {
        console.error('window.resolveLocalFileSystemURL fail', err, path);
        reject(err);
      });
    });
  }

  // 그래프 양쪽의 빈 공간을 제거하기위해서 양쪽의 NaN을 모두 잘라낸다.
  private trimNaN(data: Array<number>): Array<number> {
    // let firstIdx = data.findIndex(v => !isNaN(v));
    // firstIdx = firstIdx === -1 ? 0 : firstIdx;
    let firstIdx = 0;
    const len = data.length;
    for (; firstIdx < len - 2; ++firstIdx) {
      if (!isNaN(data[firstIdx]) && !isNaN(data[firstIdx + 1]) && !isNaN(data[firstIdx + 2])) { break; }
      ++firstIdx;
    }

    let lastIdx = len;
    // for (; lastIdx > firstIdx + 2; --lastIdx) {
      // if (!isNaN(data[lastIdx - 1]) && !isNaN(data[lastIdx - 2]) && !isNaN(data[lastIdx - 3])) { break; }
    for (; lastIdx > firstIdx + 1; --lastIdx) {
      if (!isNaN(data[lastIdx - 1]) && !isNaN(data[lastIdx - 2])) { break; }
    }

    // console.log('slice = ', firstIdx, lastIdx, data);
    const ret = data.slice(firstIdx, lastIdx);
    return ret;
  }

  public OpenConfirmReset(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.lm.OpenMsgPopup({
      title: '사용자를 변경하시겠습니까?',
      msg: '고저 표시선의 위치가<br>새로운 사용자에 맞게 조정됩니다.',
      btnOk: '변경',
      btnCancel: '취소',
    }).subscribe(data => {
      console.log(data);
      if (data === MP_RESULT.OK) {
        // 아래쪽 그래프를 표시하는 데이터(data2)를 초기화하여 그래프를 지웁니다.
        this.data2 = [];
        this.pitchSvc.reset();
      }
    });
  }


  /****************************************************************************
   * 도움말 팝업창 처리 코드
   * TODO: 나중에 음성 높낮이 팝업으로 추가 변경해야 함.
   ***************************************************************************/

  public OpenPitchHelp(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.lm.showPitchHelp = true;
  }
}
